import { useEditor } from "@craftjs/core";
import { rem } from "polished";
import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";

import useViewportContext from "@/hooks/use-viewport-context";
import { LAYOUT, MIN_MOBILE_WIDTH, MIN_TABLET_WIDTH } from "@/utils/constants";

import { withViewportStyle } from "./shared/with-viewport-style";

const ViewportDiv = withViewportStyle("div")<{
  isReady: boolean;
  isEditing: boolean;
}>`
    width: calc(100% - ${(props) => (props.isEditing ? rem(96) : 0)});
    height: 100%;
    margin: auto;
    transition: 150ms ease-out;
    opacity: ${(props) => (props.isReady ? 1 : 0)};

    ${(props) => props.$viewport.mobile`
      transform: ${(props) => (props.isEditing ? "translate(0)" : "none")};
    `}
  `;

const EDITOR_FRAME_MOBILE_WIDTH = parseInt(LAYOUT.MOBILE_MAX);
const EDITOR_FRAME_TABLET_WIDTH = parseInt(LAYOUT.TABLET_MAX);

export const ResizeViewport = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { viewport, setViewport } = useViewportContext();
  const { isEditing } = useEditor((state) => ({
    isEditing: state.options.enabled,
  }));

  const maxWidth = useMemo(() => {
    const maxWidth = "100%";

    if (!isEditing) {
      return maxWidth;
    }

    if (viewport === "mobile") {
      return MIN_MOBILE_WIDTH;
    } else if (viewport === "tablet") {
      return MIN_TABLET_WIDTH;
    }
    return maxWidth;
  }, [isEditing, viewport]);

  const [isReady, setIsReady] = useState(false);

  const handleResize = useCallback(() => {
    if (isEditing) {
      return;
    }

    const width = window.innerWidth;
    let newViewport = viewport;

    if (width <= EDITOR_FRAME_MOBILE_WIDTH) {
      newViewport = "mobile";
    } else if (width < EDITOR_FRAME_TABLET_WIDTH) {
      newViewport = "tablet";
    } else {
      newViewport = "desktop";
    }

    if (newViewport === viewport) {
      return;
    }

    setViewport(newViewport);
  }, [isEditing, viewport, setViewport]);

  useLayoutEffect(() => {
    if (!window) {
      return;
    }

    handleResize();
    setIsReady(true);

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize, setIsReady]);

  return (
    <ViewportDiv
      isEditing={isEditing}
      isReady={isReady}
      data-testid="viewport-wrapper"
      style={{ maxWidth }}
      id="viewport-wrapper"
    >
      {children}
    </ViewportDiv>
  );
};
